import * as React from "react";
import { loader } from "graphql.macro";
import { useClient, CombinedError, useQuery } from "urql";

import {
  GetChatMessagesQuery,
  GetChatMessagesQueryVariables,
} from "../graphql/GetChatMessages.generated";

type ChatMessageDataType = NonNullable<GetChatMessagesQuery["getChatMessages"]>;

type ChatMessageType = NonNullable<
  GetChatMessagesQuery["getChatMessages"]
>["messages"][0];

const GET_CHAT_MESSAGES_QUERY = loader("../graphql/GetChatMessages.graphql");

const mergeMessages = (
  prev: ChatMessageType[],
  newMessages: ChatMessageType[]
) => {
  const map = new Map<string, ChatMessageType>();
  prev.forEach((m) => map.set(m.id, m));
  newMessages.forEach((m) => map.set(m.id, m));

  return Array.from(map.values()).sort(
    (a, b) => new Date(b.created).getTime() - new Date(a.created).getTime()
  );
};

export const useChatMessages = () => {
  const [currentId, setCurrentId] = React.useState<string>("");
  const [messages, setMessages] = React.useState<ChatMessageType[]>([]);
  const [chatMessageData, setChatMessageData] = React.useState<
    ChatMessageDataType | undefined
  >();

  React.useEffect(() => {
    setMessages([]);
    setChatMessageData(undefined);
  }, [currentId]);

  const [{ data, fetching, error }] = useQuery<
    GetChatMessagesQuery,
    GetChatMessagesQueryVariables
  >({
    query: GET_CHAT_MESSAGES_QUERY,
    variables: { chatId: currentId, cursor: null, limit: 20 },
    requestPolicy: "cache-and-network",
    pause: !currentId,
  });

  React.useEffect(() => {
    if (data?.getChatMessages) {
      const serverMessages = data.getChatMessages.messages;
      setChatMessageData(data.getChatMessages);

      setMessages((prev) => {
        if (prev.length === 0) return serverMessages;

        return mergeMessages(prev, serverMessages);
      });
    }
  }, [data]);

  const client = useClient();
  const fetchMessagesMore = React.useCallback(
    async (cursor: string | null) => {
      if (!cursor) return;

      const result = await client
        .query<GetChatMessagesQuery, GetChatMessagesQueryVariables>(
          GET_CHAT_MESSAGES_QUERY,
          { chatId: currentId, cursor, limit: 20 },
          { requestPolicy: "network-only" }
        )
        .toPromise();

      if (result.data?.getChatMessages) {
        const nextBatch = result.data.getChatMessages.messages;
        setMessages((prev) => mergeMessages(prev, nextBatch));
        setChatMessageData(result.data.getChatMessages);
      }
      return result.data;
    },
    [client, currentId]
  );

  const handleSetNewMessage = React.useCallback(
    (incoming: ChatMessageType | ChatMessageType[]) => {
      const newItems = Array.isArray(incoming) ? incoming : [incoming];
      setMessages((prev) => mergeMessages(prev, newItems));
    },
    []
  );

  return React.useMemo(
    () => ({
      chatMessageData,
      messages,
      fetchMessagesMore,
      handleSetNewMessage,
      fetching,
      error,
      currentId,
      setCurrentId,
    }),
    [
      messages,
      chatMessageData,
      fetching,
      error,
      fetchMessagesMore,
      handleSetNewMessage,
      currentId,
      setCurrentId,
    ]
  );
};
